import { formatPhoneNumber, isValidPhoneNumber, parseBookingPhone } from "./phoneNumber";

export const NOTIFICATION_DESTINATIONS = [
  { value: "return_phone", label: "Return Telephone Number", channel: "sms" },
  { value: "patient_phone", label: "Patient Telephone Number", channel: "sms" },
  { value: "email", label: "Email", channel: "email" },
  { value: "none", label: "No Notifications", channel: null },
];

const DESTINATION_ORDER = ["return_phone", "patient_phone", "email", "none"];
const LEGACY_LABELS = {
  return_phone: "Return Telephone Number",
  patient_phone: "Patient Telephone Number",
  none: "No SMS Notifications",
};

export function isValidNotificationEmail(value) {
  const email = typeof value === "string" ? value.trim() : "";
  return email.length > 0 && email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function visibleNotificationDestinations(channels) {
  return NOTIFICATION_DESTINATIONS.filter((option) => {
    if (option.value === "none") return true;
    return option.channel === "sms" ? !!channels?.sms : !!channels?.email;
  });
}

export function defaultNotificationDestinations(channels) {
  return channels?.sms ? ["return_phone"] : ["none"];
}

export function normalizeNotificationDestinations(values) {
  const selected = new Set(Array.isArray(values) ? values : []);
  if (selected.has("none")) return ["none"];
  const next = DESTINATION_ORDER.filter((value) => value !== "none" && selected.has(value));
  return next.length ? next : ["none"];
}

export function toggleNotificationDestination(current, value) {
  const selected = normalizeNotificationDestinations(current);
  if (value === "none") return ["none"];
  if (selected.includes("none")) return [value];
  if (selected.includes(value)) return normalizeNotificationDestinations(selected.filter((item) => item !== value));
  return normalizeNotificationDestinations([...selected, value]);
}

export function activeNotificationDestinations(touched, selected, channels) {
  const allowed = new Set(visibleNotificationDestinations(channels).map((option) => option.value));
  const raw = touched ? selected : defaultNotificationDestinations(channels);
  return normalizeNotificationDestinations((Array.isArray(raw) ? raw : []).filter((value) => allowed.has(value)));
}

export function notificationDestinationLabel(value) {
  return LEGACY_LABELS[value] || "Patient Telephone Number";
}

export function notificationDestinationSummary(context) {
  const selected = context?.notificationDestinations;
  if (!Array.isArray(selected)) return notificationDestinationLabel(context?.smsNotificationDestination);
  if (selected.includes("none") || !selected.length) return "No Notifications";
  return selected.map((value) => {
    if (value === "email") return context.notificationEmail ? `Email (${context.notificationEmail})` : "Email";
    return NOTIFICATION_DESTINATIONS.find((item) => item.value === value)?.label || value;
  }).join(", ");
}

export function patientProfilePhone(payload) {
  const sources = [payload?.result, payload?.data, payload].filter((item) => item && typeof item === "object");
  for (const source of sources) {
    const record = Array.isArray(source) ? source[0] : source;
    const value = record?.telecom?.find((item) => item?.system === "phone")?.value;
    if (typeof value !== "string") continue;
    const phone = parseBookingPhone(value).phone;
    if (isValidPhoneNumber(phone)) return phone;
  }
  return "";
}

export function notificationDestinationHelp({ destinations, returnPhone, patientPhone, email }) {
  if (!destinations?.length || destinations.includes("none")) return "No appointment notifications are sent for this appointment.";
  const lines = [];
  if (destinations.includes("return_phone")) {
    lines.push(isValidPhoneNumber(returnPhone)
      ? `Appointment texts are sent to ${formatPhoneNumber(returnPhone)}.`
      : "Appointment texts are sent to the return telephone number.");
  }
  if (destinations.includes("patient_phone")) {
    if (patientPhone) lines.push(`Appointment texts are sent to ${formatPhoneNumber(patientPhone)} on the patient profile.`);
    else if (patientPhone === "") lines.push("No telephone number is on the patient profile. Appointment texts will not be sent.");
    else lines.push("Appointment texts are sent to the telephone number on the patient profile.");
  }
  if (destinations.includes("email")) {
    lines.push(isValidNotificationEmail(email)
      ? `Appointment emails are sent to ${email.trim()}.`
      : "Enter an email address for appointment emails.");
  }
  return lines.join(" ");
}
